// 2D shadow-casting visibility polygon: given an origin point, a set of wall segments,
// and a max vision radius, returns the polygon points describing everything the origin
// can "see" — rays are blocked by any wall segment they cross.

function raySegmentDistance(ox, oy, dx, dy, x1, y1, x2, y2) {
  const sx = x2 - x1, sy = y2 - y1;
  const denom = dx * sy - dy * sx;
  if (Math.abs(denom) < 1e-9) return null;
  const t = ((x1 - ox) * sy - (y1 - oy) * sx) / denom;
  const u = ((x1 - ox) * dy - (y1 - oy) * dx) / denom;
  if (t >= 0 && u >= 0 && u <= 1) return t;
  return null;
}

export function computeVisibilityPolygon(origin, walls, maxRadius, boundsRect) {
  const segments = [...walls.map((w) => ({ x1: w.x1, y1: w.y1, x2: w.x2, y2: w.y2 }))];
  if (boundsRect) {
    const { x, y, width, height } = boundsRect;
    segments.push(
      { x1: x, y1: y, x2: x + width, y2: y },
      { x1: x + width, y1: y, x2: x + width, y2: y + height },
      { x1: x + width, y1: y + height, x2: x, y2: y + height },
      { x1: x, y1: y + height, x2: x, y2: y }
    );
  }

  const angles = new Set();
  segments.forEach((s) => {
    [[s.x1, s.y1], [s.x2, s.y2]].forEach(([px, py]) => {
      const a = Math.atan2(py - origin.y, px - origin.x);
      angles.add(a);
      angles.add(a + 0.00001);
      angles.add(a - 0.00001);
    });
  });
  // Also cast a full ring of rays so open areas (no nearby walls) still form a smooth circle
  for (let i = 0; i < 64; i++) angles.add((i / 64) * Math.PI * 2);

  const sorted = [...angles].sort((a, b) => a - b);
  const points = [];
  for (const angle of sorted) {
    const dx = Math.cos(angle), dy = Math.sin(angle);
    let closest = maxRadius;
    for (const s of segments) {
      const t = raySegmentDistance(origin.x, origin.y, dx, dy, s.x1, s.y1, s.x2, s.y2);
      if (t !== null && t < closest) closest = t;
    }
    points.push(origin.x + dx * closest, origin.y + dy * closest);
  }
  return points;
}

// Standard ray-casting point-in-polygon test against a flat [x1,y1,x2,y2,...] points array (the
// shape returned by computeVisibilityPolygon above) — used to check whether a specific token's
// position falls inside a viewer's own vision polygon, as opposed to just a lit-but-unseen area.
export function isPointInPolygon(points, px, py) {
  let inside = false;
  const n = points.length / 2;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = points[i * 2], yi = points[i * 2 + 1];
    const xj = points[j * 2], yj = points[j * 2 + 1];
    if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

// True if no wall segment crosses the straight line between origin and target — used by the
// player-facing token context menu (Attack/Talk/Loot/Drag) so interactions honor the same
// wall-blocking rules as the fog-of-war/lighting visibility polygon above.
export function hasLineOfSight(origin, target, walls) {
  if (!walls || !walls.length) return true;
  const dx = target.x - origin.x, dy = target.y - origin.y;
  for (const w of walls) {
    const t = raySegmentDistance(origin.x, origin.y, dx, dy, w.x1, w.y1, w.x2, w.y2);
    if (t !== null && t > 0.02 && t < 0.98) return false;
  }
  return true;
}

// Returns the point just short of the first wall a straight drag path (origin -> target) would
// cross, or null if the path is clear. Used to stop a dragged token at a wall instead of letting
// it slide straight through — applied identically to desktop mouse-drag and mobile move-mode.
export function segmentBlockedByWalls(origin, target, walls) {
  if (!walls || !walls.length) return null;
  const dx = target.x - origin.x, dy = target.y - origin.y;
  const len = Math.hypot(dx, dy);
  if (len < 1) return null;
  let closestT = null;
  for (const w of walls) {
    const t = raySegmentDistance(origin.x, origin.y, dx, dy, w.x1, w.y1, w.x2, w.y2);
    if (t !== null && t > 0 && t <= 1 && (closestT === null || t < closestT)) closestT = t;
  }
  if (closestT === null) return null;
  const bufferT = Math.min(closestT, 6 / len);
  const stopT = Math.max(0, closestT - bufferT);
  return { x: origin.x + dx * stopT, y: origin.y + dy * stopT };
}

// Mirrors backend token_router.py's `_current_sight_direction` EXACTLY (same pure sine-wave math,
// no randomness/hashing) so a pivoting NPC's cone renders in the same place the server used to
// resolve a stealth check, without needing constant position broadcasts.
export function currentSightDirection(npc) {
  const base = npc.sight_direction_deg || 0;
  if (!npc.sight_pivot_enabled) return base;
  const arc = npc.sight_pivot_arc_deg || 60;
  const period = Math.max(0.5, npc.sight_pivot_period_sec || 6);
  const t0 = npc.created_at ? new Date(npc.created_at).getTime() / 1000 : 0;
  const t = Date.now() / 1000 - t0;
  let offset;
  if (npc.sight_pivot_mode === "random") {
    offset = (arc / 2) * (0.6 * Math.sin((2 * Math.PI * t) / period) + 0.4 * Math.sin((2 * Math.PI * t) / (period * 0.37) + 1.7));
  } else {
    offset = (arc / 2) * Math.sin((2 * Math.PI * t) / period);
  }
  return (base + offset + 360) % 360;
}
